/**
 * The GST state codes: the two digits a GSTIN starts with, and the place of
 * supply an invoice names. One list, so the `state` business detail, the
 * GSTIN's derived state and a template's place of supply cannot disagree.
 *
 * `ut` marks a union territory without a legislature, where UTGST is charged in
 * place of SGST. Delhi and Puducherry have legislatures, so they charge SGST.
 * Code 25 (Daman and Diu) was merged into 26 in 2020, but GSTINs issued before
 * then still start with it, so it stays readable. 97 is "Other territory" and
 * 99 the Centre's jurisdiction; neither is a place a business is registered in
 * as a state, so neither is listed.
 *
 * The names are the official English names: proper nouns, printed as data on a
 * document, never translated.
 */

export type GstState = { readonly code: string; readonly name: string; readonly ut: boolean };

export const GST_STATES: readonly GstState[] = [
  { code: '01', name: 'Jammu and Kashmir', ut: false },
  { code: '02', name: 'Himachal Pradesh', ut: false },
  { code: '03', name: 'Punjab', ut: false },
  { code: '04', name: 'Chandigarh', ut: true },
  { code: '05', name: 'Uttarakhand', ut: false },
  { code: '06', name: 'Haryana', ut: false },
  { code: '07', name: 'Delhi', ut: false },
  { code: '08', name: 'Rajasthan', ut: false },
  { code: '09', name: 'Uttar Pradesh', ut: false },
  { code: '10', name: 'Bihar', ut: false },
  { code: '11', name: 'Sikkim', ut: false },
  { code: '12', name: 'Arunachal Pradesh', ut: false },
  { code: '13', name: 'Nagaland', ut: false },
  { code: '14', name: 'Manipur', ut: false },
  { code: '15', name: 'Mizoram', ut: false },
  { code: '16', name: 'Tripura', ut: false },
  { code: '17', name: 'Meghalaya', ut: false },
  { code: '18', name: 'Assam', ut: false },
  { code: '19', name: 'West Bengal', ut: false },
  { code: '20', name: 'Jharkhand', ut: false },
  { code: '21', name: 'Odisha', ut: false },
  { code: '22', name: 'Chhattisgarh', ut: false },
  { code: '23', name: 'Madhya Pradesh', ut: false },
  { code: '24', name: 'Gujarat', ut: false },
  { code: '25', name: 'Daman and Diu', ut: true },
  { code: '26', name: 'Dadra and Nagar Haveli and Daman and Diu', ut: true },
  { code: '27', name: 'Maharashtra', ut: false },
  { code: '28', name: 'Andhra Pradesh (before 2014)', ut: false },
  { code: '29', name: 'Karnataka', ut: false },
  { code: '30', name: 'Goa', ut: false },
  { code: '31', name: 'Lakshadweep', ut: true },
  { code: '32', name: 'Kerala', ut: false },
  { code: '33', name: 'Tamil Nadu', ut: false },
  { code: '34', name: 'Puducherry', ut: false },
  { code: '35', name: 'Andaman and Nicobar Islands', ut: true },
  { code: '36', name: 'Telangana', ut: false },
  { code: '37', name: 'Andhra Pradesh', ut: false },
  { code: '38', name: 'Ladakh', ut: true },
];

const BY_CODE: ReadonlyMap<string, GstState> = new Map(GST_STATES.map((one) => [one.code, one]));

/** The state a code names, or null. */
export const gstStateOf = (code: string): GstState | null => BY_CODE.get(code) ?? null;

const fold = (text: string): string => text.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * A code from what a person typed: the code itself (`27`, or `7` for `07`), or
 * the state's name in any case (`maharashtra`). Null when it names no state.
 */
export const gstStateCodeOf = (text: string): string | null => {
  const raw = text.trim();
  if (/^\d{1,2}$/.test(raw)) {
    const code = raw.padStart(2, '0');
    return BY_CODE.has(code) ? code : null;
  }
  const folded = fold(raw);
  if (folded === '') return null;
  return GST_STATES.find((one) => fold(one.name) === folded)?.code ?? null;
};
